import { useState } from 'react';
import { X, Settings, Plus, Edit2, Trash2, ChevronUp, ChevronDown, AlertCircle } from 'lucide-react';
import { DojosRow, SeasonsRow } from '../types/database';
import { dojoSeasonService } from '../modules/stats/dojoSeasonService';
import CustomSelect from './CustomSelect';

interface DojoSeasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  dojos: DojosRow[];
  seasons: SeasonsRow[];
  onDojosChange: (dojos: DojosRow[]) => void;
  onSeasonsChange: (seasons: SeasonsRow[]) => void;
}

export default function DojoSeasonModal({
  isOpen,
  onClose,
  dojos,
  seasons,
  onDojosChange,
  onSeasonsChange,
}: DojoSeasonModalProps) {
  const [newDojoName, setNewDojoName] = useState('');
  const [newSeasonName, setNewSeasonName] = useState('');
  const [newSeasonDojoId, setNewSeasonDojoId] = useState<string>('');
  const [editingDojo, setEditingDojo] = useState<DojosRow | null>(null);
  const [editingSeason, setEditingSeason] = useState<SeasonsRow | null>(null);
  const [editDojoName, setEditDojoName] = useState('');
  const [editSeasonName, setEditSeasonName] = useState('');
  const [editSeasonDojoId, setEditSeasonDojoId] = useState<string>('');
  const [deletingDojo, setDeletingDojo] = useState<DojosRow | null>(null);
  const [deletingSeason, setDeletingSeason] = useState<SeasonsRow | null>(null);
  const [dojoListExpanded, setDojoListExpanded] = useState(false);
  const [seasonListExpanded, setSeasonListExpanded] = useState(false);
  const [dojoDeleteError, setDojoDeleteError] = useState('');

  if (!isOpen) return null;

  const handleAddDojo = async () => {
    try {
      const newDojo = await dojoSeasonService.addDojo(newDojoName, dojos);
      if (newDojo) {
        onDojosChange([...dojos, newDojo]);
        setNewDojoName('');
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateDojo = async () => {
    if (!editingDojo) return;
    try {
      await dojoSeasonService.updateDojo(editingDojo.id, editDojoName);
      onDojosChange(dojos.map(d => d.id === editingDojo.id ? { ...d, name: editDojoName.trim() } : d));
      setEditingDojo(null);
      setEditDojoName('');
    } catch (err) {
      alert('更新道场失败');
    }
  };

  const handleDeleteDojo = async () => {
    if (!deletingDojo) return;
    try {
      await dojoSeasonService.deleteDojo(deletingDojo.id);
      onDojosChange(dojos.filter(d => d.id !== deletingDojo.id));
      setDeletingDojo(null);
    } catch (err: any) {
      setDojoDeleteError(err.message || '删除道场失败');
    }
  };

  const handleAddSeason = async () => {
    try {
      const newSeason = await dojoSeasonService.addSeason(newSeasonName, newSeasonDojoId, seasons);
      if (newSeason) {
        onSeasonsChange([...seasons, newSeason]);
        setNewSeasonName('');
        setNewSeasonDojoId('');
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateSeason = async () => {
    if (!editingSeason) return;
    try {
      await dojoSeasonService.updateSeason(editingSeason.id, editSeasonName, editSeasonDojoId);
      onSeasonsChange(seasons.map(s => s.id === editingSeason.id ? {
        ...s,
        name: editSeasonName.trim(),
        dojo_id: Number(editSeasonDojoId),
      } : s));
      setEditingSeason(null);
      setEditSeasonName('');
      setEditSeasonDojoId('');
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteSeason = async () => {
    if (!deletingSeason) return;
    try {
      await dojoSeasonService.deleteSeason(deletingSeason.id);
      onSeasonsChange(seasons.filter(s => s.id !== deletingSeason.id));
      setDeletingSeason(null);
    } catch (err) {
      alert('删除赛季失败');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-purple-500/30 shadow-2xl shadow-purple-500/20">
        <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-avalon-gold" />
            道场与赛季管理
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          <div>
            <button
              onClick={() => setDojoListExpanded(!dojoListExpanded)}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <h3 className="text-white font-semibold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-lg">🏯</span>
                道场列表 ({dojos.length})
              </h3>
              {dojoListExpanded ? <ChevronUp className="w-5 h-5 text-avalon-text" /> : <ChevronDown className="w-5 h-5 text-avalon-text" />}
            </button>
            {dojoListExpanded && (
              <>
                <div className="space-y-2 mb-3 mt-2">
                  {dojos.map((dojo) => (
                    <div key={dojo.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                      <span className="text-white">{dojo.name}</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingDojo(dojo);
                            setEditDojoName(dojo.name || '');
                          }}
                          className="p-2 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingDojo(dojo);
                            setDojoDeleteError('');
                          }}
                          className="p-2 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="输入新道场名称"
                    value={newDojoName}
                    onChange={(e) => setNewDojoName(e.target.value)}
                    className="flex-1 p-2 rounded-lg bg-white/5 text-white border border-white/20 focus:border-avalon-purple outline-none text-sm"
                  />
                  <button
                    onClick={handleAddDojo}
                    disabled={!newDojoName.trim()}
                    className="px-4 py-2 rounded-lg bg-avalon-purple text-white hover:bg-avalon-purple/80 transition-colors disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div>
            <button
              onClick={() => setSeasonListExpanded(!seasonListExpanded)}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              <h3 className="text-white font-semibold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-avalon-gold to-orange-500 flex items-center justify-center text-lg">🏆</span>
                赛季列表 ({seasons.length})
              </h3>
              {seasonListExpanded ? <ChevronUp className="w-5 h-5 text-avalon-text" /> : <ChevronDown className="w-5 h-5 text-avalon-text" />}
            </button>
            {seasonListExpanded && (
              <>
                <div className="space-y-2 mb-3 mt-2">
                  {seasons.map((season) => {
                    const dojo = dojos.find(d => d.id === season.dojo_id);
                    return (
                      <div key={season.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                        <div>
                          <span className="text-white">{dojo ? `${dojo.name} - ${season.name}` : season.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingSeason(season);
                              setEditSeasonName(season.name || '');
                              setEditSeasonDojoId(season.dojo_id ? String(season.dojo_id) : '');
                            }}
                            className="p-2 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingSeason(season)}
                            className="p-2 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2">
                  <div className="border-2 border-avalon-purple rounded-lg p-3 bg-avalon-purple/10">
                    <label className="block text-avalon-gold text-sm mb-2 font-medium">
                      <span className="text-red-500">*</span> 所属道场（必填）
                    </label>
                    <CustomSelect
                      value={newSeasonDojoId}
                      onChange={setNewSeasonDojoId}
                      options={dojos.length === 0
                        ? [{ value: '', label: '请先创建道场' }]
                        : [{ value: '', label: '请选择所属道场' }, ...dojos.map(d => ({ value: String(d.id), label: d.name || '' }))]
                      }
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="输入新赛季名称"
                      value={newSeasonName}
                      onChange={(e) => setNewSeasonName(e.target.value)}
                      className="flex-1 p-2 rounded-lg bg-white/5 text-white border border-white/20 focus:border-avalon-purple outline-none text-sm"
                    />
                    <button
                      onClick={() => {
                        if (!newSeasonDojoId) {
                          alert('请先选择所属道场');
                          return;
                        }
                        handleAddSeason();
                      }}
                      disabled={!newSeasonName.trim()}
                      className="px-4 py-2 rounded-lg bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400 transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {editingDojo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-sm w-full border border-purple-500/30 shadow-2xl shadow-purple-500/20">
            <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-400" />
                编辑道场
              </h2>
              <button
                onClick={() => { setEditingDojo(null); setEditDojoName(''); }}
                className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-avalon-text text-sm mb-2">道场名称</label>
                <input
                  type="text"
                  value={editDojoName}
                  onChange={(e) => setEditDojoName(e.target.value)}
                  className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-avalon-purple outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setEditingDojo(null); setEditDojoName(''); }}
                  className="flex-1 py-3 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleUpdateDojo}
                  disabled={!editDojoName.trim()}
                  className="flex-1 py-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deletingDojo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-sm w-full border border-red-500/30 shadow-2xl shadow-red-500/20">
            <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                确认删除
              </h2>
              <button
                onClick={() => { setDeletingDojo(null); setDojoDeleteError(''); }}
                className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-avalon-text">
                确定要删除【<span className="text-white font-medium">{deletingDojo.name}</span>】吗？此操作不可恢复。
              </p>
              {dojoDeleteError && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
                  {dojoDeleteError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setDeletingDojo(null); setDojoDeleteError(''); }}
                  className="flex-1 py-3 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteDojo}
                  className="flex-1 py-3 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingSeason && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-sm w-full border border-purple-500/30 shadow-2xl shadow-purple-500/20">
            <div className="p-4 border-b border-purple-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-400" />
                编辑赛季
              </h2>
              <button
                onClick={() => { setEditingSeason(null); setEditSeasonName(''); setEditSeasonDojoId(''); }}
                className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <CustomSelect
                label="所属道场"
                value={editSeasonDojoId}
                onChange={setEditSeasonDojoId}
                options={dojos.length === 0
                  ? [{ value: '', label: '请先创建道场' }]
                  : [{ value: '', label: '请选择所属道场' }, ...dojos.map(d => ({ value: String(d.id), label: d.name || '' }))]
                }
              />
              <div>
                <label className="block text-avalon-text text-sm mb-2">赛季名称</label>
                <input
                  type="text"
                  value={editSeasonName}
                  onChange={(e) => setEditSeasonName(e.target.value)}
                  className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-avalon-purple outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setEditingSeason(null); setEditSeasonName(''); setEditSeasonDojoId(''); }}
                  className="flex-1 py-3 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleUpdateSeason}
                  disabled={!editSeasonName.trim() || !editSeasonDojoId}
                  className="flex-1 py-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deletingSeason && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-sm w-full border border-red-500/30 shadow-2xl shadow-red-500/20">
            <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                确认删除
              </h2>
              <button
                onClick={() => setDeletingSeason(null)}
                className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {(() => {
                const dojo = dojos.find(d => d.id === deletingSeason.dojo_id);
                return (
                  <p className="text-avalon-text">
                    确定要删除【<span className="text-white font-medium">{dojo ? `${dojo.name} - ${deletingSeason.name}` : deletingSeason.name}</span>】吗？此操作不可恢复。
                  </p>
                );
              })()}
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingSeason(null)}
                  className="flex-1 py-3 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteSeason}
                  className="flex-1 py-3 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  确认删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
